import i18next from 'i18next';
import pickBy from 'lodash/pickBy';
import { TFuncKey } from 'react-i18next';
import { OneComposerEditorType } from '~/components/OneComposer/OneComposer.types';
import { TEditorType } from '~/components/OneComposer/useOneComposerSmartfields';
import { NAMESPACES } from '~/i18n/namespaces';
import { SmartFieldAdapter } from '~/services/adapters/smartfield.adapter';
import {
    CUSTOM_CLIENT_FIELD_KEY_PREFIX,
    CUSTOM_SECOND_CLIENT_FIELD_KEY_PREFIX,
    IServerSmartField,
    ONE_COMPOSER_SMART_FIELD_KEYS,
    SMART_FIELD_KEYS,
    TGetFlowSmartFieldsRequestParams,
    TGetOneComposerSmartFieldsRequestParams,
    TSmartFieldKeys,
    customClientFieldKeys
} from '~/common/SmartFields/server.types';
import {
    TOneComposerSmartFields,
    TSmartFields
} from '~/common/SmartFields/smart-fields.types';
import { SMART_FIELDS_MARKER } from '~/common/SmartFields/smart-fields.constants';
import { LoggerService } from '~/services/logger/logger.service';
import { smartfieldsLoggerProps } from '~/common/SmartFields/smart-fields.utils';

const SMART_FIELD_KEY_TO_TITLE_TRANS_KEY: Record<
    TSmartFieldKeys,
    TFuncKey<typeof NAMESPACES.SMART_FIELDS, 'smart_fields'>
> = {
    'project.event_name': 'project_event_name',
    'project.event_date': 'project_event_date',
    'project.event_end_date': 'project_event_end_date',
    'project.event_time_start': 'project_event_time_start',
    'project.event_time_end': 'project_event_time_end',
    'project.event_location': 'project_event_location',
    'project.project_type': 'project_project_type',
    'project.event_budget': 'project_event_budget',
    'project.event_guests': 'project_event_guests',
    'client.full_name': 'client_full_name',
    'client.first_name': 'client_first_name',
    'client.last_name': 'client_last_name',
    'client.email': 'client_email',
    'client.phone_number': 'client_phone_number',
    'client.address': 'client_address',
    'client.initials': 'client_initials',
    'client.organization_name': 'client_organization_name',
    'second_client.full_name': 'second_client_full_name',
    'second_client.first_name': 'second_client_first_name',
    'second_client.last_name': 'second_client_last_name',
    'second_client.email': 'second_client_email',
    'second_client.phone_number': 'second_client_phone_number',
    'second_client.address': 'second_client_address',
    'second_client.initials': 'second_client_initials',
    'second_client.organization_name': 'second_client_organization_name',
    'company.company_name': 'company_company_name',
    'company.website_url': 'company_website_url',
    'company.email': 'company_email',
    'company.phone_number': 'company_phone_number',
    'company.address': 'company_address',
    'time.today': 'time_today',
    'user.full_name': 'user_full_name',
    'user.first_name': 'user_first_name',
    'user.last_name': 'user_last_name',
    'user.email': 'user_email',
    'user.phone_number': 'user_phone_number',
    'user.address': 'user_address',
    'user.initials': 'user_initials',
    'payment.total_price': 'payment_total_price',
    'payment.sub_total': 'payment_sub_total',
    'payment.invoice_number': 'payment_invoice_number',
    'payment.first_payment_amount': 'payment_first_payment_amount',
    'payment.first_payment_due_date': 'payment_first_payment_due_date',
    'payment.second_payment_amount': 'payment_second_payment_amount',
    'payment.second_payment_due_date': 'payment_second_payment_due_date',
    'payment.third_payment_amount': 'payment_third_payment_amount',
    'payment.third_payment_due_date': 'payment_third_payment_due_date',
    'payment.last_payment_amount': 'payment_last_payment_amount',
    'payment.last_payment_due_date': 'payment_last_payment_due_date',
    'session.date_time_start': 'session_date_time_start',
    'session.date_start': 'session_date_start',
    'session.meeting_location': 'session_meeting_location',
    'session.session_name': 'session_session_name'
};

export const smartFieldTranslated = (key: TSmartFieldKeys, t) => {
    return smartFieldTransKey(key) ? t(smartFieldTransKey(key)) : key;
};

export const smartFieldTransKey = (key: TSmartFieldKeys) =>
    SMART_FIELD_KEY_TO_TITLE_TRANS_KEY[key];

const SMART_FIELDS_ADJUSTMENTS_BY_EDITOR_TYPE: Partial<
    Record<
        TEditorType,
        {
            filterOutSourceFields?: Array<IServerSmartField['source']>;
            supportedEmptyFields?: Array<IServerSmartField['source']>;
        }
    >
> = {
    ['legacyAutomationEditor']: {
        supportedEmptyFields: ['client', 'company', 'project', 'time', 'user']
    },
    [OneComposerEditorType.BatchEmailEditor]: {
        filterOutSourceFields: ['project'],
        supportedEmptyFields: ['client', 'company', 'project', 'time', 'user']
    },
    [OneComposerEditorType.ContactViewProjectEditor]: {
        filterOutSourceFields: [
            'project',
            'second_client',
            'second_client_custom_fields'
        ]
    },
    [OneComposerEditorType.SessionReminderModal]: {
        filterOutSourceFields: ['time'],
        supportedEmptyFields: ['client', 'company', 'user']
    },
    [OneComposerEditorType.BulkContactsModal]: {
        filterOutSourceFields: ['project'],
        supportedEmptyFields: ['client']
    },
    [OneComposerEditorType.BulkWorkspacesModal]: {
        supportedEmptyFields: ['client', 'project']
    },
    [OneComposerEditorType.EmailTemplateEditor]: {
        supportedEmptyFields: ['client', 'company', 'project', 'time', 'user']
    },
    [OneComposerEditorType.SendClientPortalLinkSettingsModal]: {
        filterOutSourceFields: ['project'],
        supportedEmptyFields: ['client']
    }
};

type TServerSmartField = Partial<Record<TSmartFieldKeys, IServerSmartField>>;

export class SmartFieldService extends SmartFieldAdapter {
    logger = new LoggerService(smartfieldsLoggerProps);

    async getFlowSmartFields({
        flowId,
        isTemplateEditor,
        workspaceId
    }: TGetFlowSmartFieldsRequestParams): Promise<TSmartFields> {
        try {
            const serverSmartFields = await super.getFlowSmartFields({
                flowId,
                isTemplateEditor,
                workspaceId
            });
            return this.transformServerSmartFields({
                serverSmartFields
            });
        } catch (error) {
            this.logger.trackError({
                error,
                message: '@smartfield.service: Failed to get flow smart fields',
                context: {
                    flowId,
                    isTemplateEditor,
                    workspaceId
                }
            });
            return this.getFlowPlaceholderFields();
        }
    }

    async fetchOneComposerSmartFields({
        editorType,
        onError,
        smartFieldContext
    }: TGetOneComposerSmartFieldsRequestParams & {
        editorType: TEditorType;
        onError?: () => void;
        smartFieldContext: TGetOneComposerSmartFieldsRequestParams;
    }): Promise<TOneComposerSmartFields> {
        try {
            const serverSmartFields = await super.getOneComposerSmartFields(
                smartFieldContext
            );
            return this.transformServerSmartFields({
                serverSmartFields,
                editorType
            });
        } catch (error) {
            this.logger.trackError({
                error,
                message: '@smartfield.service: Failed to get smart fields',
                context: { editorType, smartFieldContext }
            });
            onError?.();
            return this.getOneComposerPlaceholderFields();
        }
    }

    transformServerSmartFields({
        serverSmartFields,
        editorType
    }: {
        serverSmartFields: TServerSmartField;
        editorType?: TEditorType;
    }) {
        return this.translateSmartFields(
            this.filterSmartFieldsByContext(serverSmartFields, editorType),
            editorType
        );
    }

    filterSmartFieldsByContext(
        smartFields: TServerSmartField,
        editorType?: TEditorType
    ): TServerSmartField {
        return pickBy(smartFields, smartField => {
            const { filterOutSourceFields } =
                SMART_FIELDS_ADJUSTMENTS_BY_EDITOR_TYPE[editorType] || {};
            if (!filterOutSourceFields) {
                return true;
            }
            return !filterOutSourceFields.includes(smartField.source);
        });
    }

    getPlaceholderFields(keys): TSmartFields | TOneComposerSmartFields {
        const t = i18next.getFixedT(
            'en',
            NAMESPACES.SMART_FIELDS,
            'smart_fields'
        );
        return keys.reduce((acc: TSmartFields, key) => {
            const title = smartFieldTranslated(key, t);
            const source = key.slice(
                0,
                key.indexOf('.')
            ) as IServerSmartField['source'];
            acc[key] = {
                fieldKey: key,
                key,
                id: `${SMART_FIELDS_MARKER}${key}`,
                is_valid: false,
                is_available: false,
                source,
                text: `{${title}}`,
                title,
                subtitle: '',
                value: ''
            };
            return acc;
        }, {});
    }

    getFlowPlaceholderFields(): TSmartFields {
        return this.getPlaceholderFields(SMART_FIELD_KEYS);
    }

    getOneComposerPlaceholderFields(): TOneComposerSmartFields {
        return this.getPlaceholderFields(ONE_COMPOSER_SMART_FIELD_KEYS);
    }

    translateSmartFields(
        smartFields: TServerSmartField,
        editorType: TEditorType
    ): TOneComposerSmartFields {
        const t = i18next.getFixedT(
            'en',
            NAMESPACES.SMART_FIELDS,
            'smart_fields'
        );

        const getFieldTitle = (sf: IServerSmartField) => {
            const translation = smartFieldTranslated(sf.key, t);
            if (sf.source === CUSTOM_CLIENT_FIELD_KEY_PREFIX) {
                return t('first_client_custom_field', {
                    customFieldName: sf.key
                });
            }
            if (sf.source === CUSTOM_SECOND_CLIENT_FIELD_KEY_PREFIX) {
                return t('second_client_custom_field', {
                    customFieldName: sf.key
                });
            }
            return translation === 'smart_fields.undefined'
                ? sf.key
                : translation;
        };

        return Object.values(smartFields).reduce(
            (acc: TOneComposerSmartFields, smartField) => {
                const { supportedEmptyFields } =
                    SMART_FIELDS_ADJUSTMENTS_BY_EDITOR_TYPE[editorType] || {};

                const isAllowedEmpty = !!supportedEmptyFields?.includes(
                    smartField.source
                );

                const title = getFieldTitle(smartField);

                const placeholder = `{${title}}`;
                const subtitle =
                    smartField.value || (isAllowedEmpty ? '' : t('no_data'));

                const fieldIdentifier = smartField?.linked_id
                    ? `${smartField.source}.${smartField.linked_id}`
                    : smartField.key;

                const translatedKey = customClientFieldKeys.includes(
                    smartField.source
                )
                    ? `${smartField.source}.${smartField.key}`
                    : smartField.key;

                acc[translatedKey] = {
                    fieldKey: fieldIdentifier,
                    id: `${SMART_FIELDS_MARKER}${fieldIdentifier}`,
                    is_valid: smartField.is_available || isAllowedEmpty,
                    is_available: smartField.is_available,
                    is_suggested: smartField.is_suggested,
                    key: translatedKey,
                    title, // title is what used in the autocomplete menu item to describe the field
                    subtitle, // subtitle is what used to show the current known value in the dropdown menu
                    source: smartField.source,
                    text: smartField.value?.toString() || placeholder,
                    value: smartField.value?.toString(), // text is what actually used in the editor content
                    linked_id: smartField?.linked_id
                };

                return acc;
            },
            {}
        );
    }
}
